import { freeDailyCards, freeDailyExercises } from "../fuxi/free-day.js";
import { historyLimits } from "./history-limits.js";
import {
  recordingAllowance,
  translationAllowance,
} from "./model-allowance-rules.js";
import { freeImportRows, proImportRows } from "./import-bounds.js";
import { uploadFileBytes } from "./import-upload-bounds.js";
import {
  dailyPronunciationAttempts,
  freeDailyPronunciationAttempts,
} from "./pronunciation-allowance.js";
import { freeSavedLiju } from "./saved-liju.js";
import { spokenAllowance } from "./model-allowance-rules.js";
import { freeSavedWords } from "./saved-word.js";
import { freeStartedLists } from "./started-list.js";

/**
 * What a free account holds and what Pro 专业 adds, as one table, and every
 * offer of Pro the site makes.
 *
 * Pro used to be offered in a dozen places, each in its own words and each
 * linking the account page or nothing. Every one of them now draws its line
 * from {@link proFeatures} and its link from {@link proOfferHref}, so a limit
 * that moves moves everywhere and no offer can promise what the code does not
 * grant. `.claude/notes/conversions-strategy.md` under "What each tier holds"
 * is the table this was written from, and #1079 the issue.
 *
 * **Every figure is read off the constant that enforces it.** A free reader's
 * 300 words is `freeSavedWords`, their 20 cards is `freeDailyCards`, and so on
 * down the table. `pro-offer.iso.test.ts` checks the gates against the same
 * constants, so a gate that stops reading one fails there rather than leaving
 * this table describing a limit nobody applies.
 *
 * **It imports nothing that a browser cannot run.** The segmenter, the Exercise
 * finish and the extension all draw from it, which is why `free-day.ts` and
 * `history-limits.ts` stand apart from the modules that re-export them. The
 * Chinese is carried as plain strings, so each caller wraps it in its own
 * bilingual treatment: `bilingual` under `/user/`, `said` in the extension,
 * text on a page script.
 *
 * **The `/pro/` page (#1078) is drawn from the same table.** It will be written
 * into `articles/data/pro.json` by `pnpm run assets`, beside `assets.json`, for
 * Hugo to read. A build writes it before Hugo runs, so the page and this table
 * cannot disagree, and nothing generated is committed to go stale.
 */

/** A piece of copy in English and in both writings of Chinese. */
export interface OfferWords {
  readonly english: string;
  readonly simplified: string;
  readonly traditional: string;
}

/**
 * Copy whose Chinese is written alike in both writings unless told otherwise.
 * @param english - The English, which leads.
 * @param simplified - The Chinese in 简体.
 * @param traditional - The Chinese in 繁體, where it differs.
 * @returns The three.
 */
function words(
  english: string,
  simplified: string,
  traditional = simplified,
): OfferWords {
  return { english, simplified, traditional };
}

/** A number grouped the way the rest of the site groups it. */
function grouped(figure: number): string {
  return figure.toLocaleString("en-GB");
}

/**
 * Where an offer of Pro leads, which is the public page that explains and
 * prices it (#1078).
 *
 * It was the account page until that page shipped, since an offer linking
 * `/pro/` before then would have been a 404 in every email the site sends. The
 * placement goes on the query either way, so presses are counted by where they
 * came from.
 */
export const proOfferPath = "/pro/";

/**
 * What Pro costs, in US dollars with tax included, as Checkout charges it.
 *
 * The Prices themselves live in Stripe under the lookup keys in
 * `src/billing/pro-plans.ts`, and Checkout reads them there. These are the
 * figures the site says, on `/pro/` and in the account panel, written once.
 */
export const proPrice = { monthly: 8, yearly: 80 } as const;

/**
 * What the yearly plan saves against a year paid month by month (CB-61).
 *
 * `/pro/` and the account page's price box both say it beside the yearly
 * price, as two months free. Both figures are worked out from
 * {@link proPrice}, so a price that moves moves the saving with it. A yearly
 * price that is no whole number of monthly payments has no number of months
 * to name, and throws here rather than printing a fraction on the page.
 */
export const yearlySaving = ((): {
  readonly monthlyYear: number;
  readonly monthsFree: number;
} => {
  const monthsPaid = proPrice.yearly / proPrice.monthly;

  if (!Number.isInteger(monthsPaid) || monthsPaid >= 12) {
    throw new Error(
      `$${String(proPrice.yearly)} a year is not a whole number of months below $${String(proPrice.monthly * 12)}`,
    );
  }

  return { monthlyYear: proPrice.monthly * 12, monthsFree: 12 - monthsPaid };
})();

/** A count of months, written out the way the price box says it. */
const monthWords = ["One", "Two", "Three", "Four", "Five", "Six"];

/**
 * The words beside the yearly price, as "Two months free".
 *
 * English only, like every offer of Pro.
 */
export const yearlySavingLabel = `${monthWords[yearlySaving.monthsFree - 1] ?? String(yearlySaving.monthsFree)} ${yearlySaving.monthsFree === 1 ? "month" : "months"} free`;

/** The line beside it, as "$80 a year, compared with $96 if you pay monthly". */
export const yearlySavingLine = `$${String(proPrice.yearly)} a year, compared with $${String(yearlySaving.monthlyYear)} if you pay monthly`;

/**
 * Where an offer was pressed, which travels on the link as `from`.
 *
 * The lean report (#1208) counts presses on each placement from the
 * CloudFront logs, grouping requests for {@link proOfferPath} by this value.
 * A request without it is somebody who went there on their own. `pro` is the
 * button on `/pro/` itself, which leads to the account page's Subscribe.
 */
export type OfferPlacement =
  | "account"
  | "account-menu"
  | "booklet"
  | "ci-cap"
  | "ci-priority"
  | "cidan-second-list"
  | "email-backup"
  | "email-due"
  | "email-huigu"
  | "email-keep-studying"
  | "email-start-studying"
  | "email-welcome"
  | "email-win-back"
  | "extension"
  | "fayin"
  | ChromePlacement
  | FeaturePlacement
  | "fuxi-done"
  | "fuxi-settings"
  | "fuxi-speech"
  | "history"
  | "home"
  | "huigu-mail"
  | "import-upload"
  | "lianxi-finish"
  | "lianxi-gate"
  | "lianxi-speech"
  | "lianxi-starts"
  | "liju-cap"
  | "liju-priority"
  | "liju-recording"
  | "liju-translation"
  | "milestone-fuxi"
  | "milestone-words"
  | "own-lists"
  | PracticePlacement
  | "pro"
  | "segmenter";

/**
 * The offers of Pro in a page's own chrome, each the free reader's counterpart
 * of a sign-up offer in the same place (CB-61).
 *
 * `strap` is the line at the top of an article, a 词典 entry or a 例句,
 * `foot-card` the card at its foot, `footer` the line in the site's footer,
 * `rail` the plan panel in the rail and `home-study` the line under the home
 * page's Continue studying card. Hugo writes every one but `strap` on the 词典
 * and the 例句, which `src/page-render/account-strap-markup.ts` writes.
 */
export type ChromePlacement =
  | "foot-card"
  | "footer"
  | "home-study"
  | "rail"
  | "strap";

/**
 * A page that teaches a way of studying and shows the feature that does it
 * (#1212).
 *
 * `/chinese-flashcards/`, `/anki-chinese/` and
 * `/mandarin-chinese-pronunciation-practice/` are drawn by
 * `layouts/page/feature.html`, which writes the link from the page's
 * `proPlacement`, and the method passage on `/lianxi/` is `lianxi-method`.
 * Hugo writes all four, so nothing in TypeScript calls {@link proOfferHref}
 * with them, and the union is where the report's reader finds what they mean.
 */
export type FeaturePlacement =
  | "feature-anki"
  | "feature-flashcards"
  | "feature-pronunciation"
  | "lianxi-method";

/**
 * The practice panel on a public page, by the kind of page it is drawn on
 * (#1222).
 *
 * The panel offers the free practice a grammar article, a single-character
 * 词典 entry or a 例句 already has. Its links to that practice carry the
 * placement through {@link practiceHref}, and its one line about Pro, which
 * only a reader on a free account sees, carries it through
 * {@link proOfferHref}. `src/analytics/practice-offer.ts` counts the first
 * kind and `src/analytics/pro-offer.ts` the second, both by page type.
 */
export type PracticePlacement =
  | "practice-grammar"
  | "practice-cidian"
  | "practice-liju";

/** The rows of the table, which are also the sections of `/pro/`. */
export type ProFeatureId =
  | "saved-words"
  | "saved-liju"
  | "own-liju-translation"
  | "own-liju-recording"
  | "fuxi"
  | "exercises"
  | "custom-grammar"
  | "lists"
  | "priority"
  | "paste-import"
  | "file-import"
  | "history"
  | "huigu"
  | "backup"
  | "booklet"
  | "export"
  | "pronunciation"
  | "speaking";

/** One row: what the feature is called, and what each kind of account gets. */
export interface ProFeature {
  /** The row's id, which is also the fragment its section on `/pro/` takes. */
  readonly id: ProFeatureId;
  readonly name: OfferWords;
  readonly free: OfferWords;
  readonly pro: OfferWords;
}

/** No limit, said the one way wherever the table says it. */
const noLimit = words("No limit", "不限", "不限");

/** No daily limit, for the two allowances that turn over each day. */
const noDailyLimit = words("No daily limit", "每日数量不限", "每日數量不限");

/** What a free account cannot do at all. */
const notIncluded = words("Not included", "不包含", "不包含");

const freeHistory = historyLimits(false);
const proHistory = historyLimits(true);

/** A lookup history allowance, as a cell of the table. */
function historyCell(limits: { limit: number; days: number }): OfferWords {
  const limit = grouped(limits.limit);
  const days = String(limits.days);

  return words(
    `Up to ${limit} words, kept for ${days} days after each lookup`,
    `最多 ${limit} 条记录，每条从查询时起最多保留 ${days} 天`,
    `最多 ${limit} 條記錄，每條從查詢時起最多保留 ${days} 天`,
  );
}

/**
 * The table, in the order `/pro/` draws it.
 *
 * Extension translation is left out until a reader can see their allowance
 * (#1096). A row a reader cannot check is a promise they cannot hold the site
 * to.
 */
export const proFeatures: readonly ProFeature[] = [
  {
    id: "saved-words",
    name: words("Saved words", "收藏的词", "收藏的詞"),
    free: words(
      `${grouped(freeSavedWords)} saved words`,
      `最多可收藏 ${grouped(freeSavedWords)} 个词`,
      `最多可收藏 ${grouped(freeSavedWords)} 個詞`,
    ),
    pro: noLimit,
  },
  {
    id: "saved-liju",
    name: words("Saved sentences 例句", "收藏的例句"),
    free: words(
      `${grouped(freeSavedLiju)} saved sentences`,
      `最多可收藏 ${grouped(freeSavedLiju)} 个例句`,
      `最多可收藏 ${grouped(freeSavedLiju)} 個例句`,
    ),
    pro: noLimit,
  },
  {
    id: "own-liju-translation",
    name: words(
      "Translations of your own sentences",
      "自编例句翻译",
      "自編例句翻譯",
    ),
    free: words(
      `${String(translationAllowance.free)} translations a day`,
      `每天 ${String(translationAllowance.free)} 次`,
      `每天 ${String(translationAllowance.free)} 次`,
    ),
    pro: words(
      `${String(translationAllowance.pro)} translations a day`,
      `每天 ${String(translationAllowance.pro)} 次`,
      `每天 ${String(translationAllowance.pro)} 次`,
    ),
  },
  {
    id: "own-liju-recording",
    name: words(
      "Recordings of your own sentences",
      "自编例句录音",
      "自編例句錄音",
    ),
    free: words(
      `${String(recordingAllowance.free)} recordings a day`,
      `每天录音 ${String(recordingAllowance.free)} 次`,
      `每天錄音 ${String(recordingAllowance.free)} 次`,
    ),
    pro: words(
      `${String(recordingAllowance.pro)} recordings a day`,
      `每天录音 ${String(recordingAllowance.pro)} 次`,
      `每天錄音 ${String(recordingAllowance.pro)} 次`,
    ),
  },
  {
    id: "fuxi",
    name: words("Review cards 复习", "复习卡片", "複習卡片"),
    free: words(
      `${String(freeDailyCards)} cards a day`,
      `每天 ${String(freeDailyCards)} 张卡片`,
      `每天 ${String(freeDailyCards)} 張卡片`,
    ),
    pro: noDailyLimit,
  },
  {
    id: "exercises",
    name: words("New Exercises 练习", "新练习", "新練習"),
    free: words(
      `${String(freeDailyExercises)} new Exercises a day`,
      `每天可开始 ${String(freeDailyExercises)} 个新练习`,
      `每天可開始 ${String(freeDailyExercises)} 個新練習`,
    ),
    pro: noDailyLimit,
  },
  {
    id: "custom-grammar",
    name: words("Custom grammar practice", "自选语法练习", "自選語法練習"),
    free: words("Standard sets available", "可使用标准练习", "可使用標準練習"),
    pro: words(
      "Choose studied targets, length and formats",
      "选择学过的语法、题数和题型",
      "選擇學過的語法、題數和題型",
    ),
  },
  {
    id: "pronunciation",
    name: words("Pronunciation feedback", "发音反馈", "發音反饋"),
    free: words(
      `Up to ${String(freeDailyPronunciationAttempts)} checked recordings a day`,
      `每天最多检查 ${String(freeDailyPronunciationAttempts)} 次录音`,
      `每天最多檢查 ${String(freeDailyPronunciationAttempts)} 次錄音`,
    ),
    pro: words(
      `${String(dailyPronunciationAttempts)} checked recordings a day`,
      `每天检查 ${String(dailyPronunciationAttempts)} 次录音`,
      `每天檢查 ${String(dailyPronunciationAttempts)} 次錄音`,
    ),
  },
  {
    id: "speaking",
    name: words("Speaking", "语音答题", "語音答題"),
    free: words(
      `Up to ${String(spokenAllowance.free)} spoken answers a day`,
      `每天最多 ${String(spokenAllowance.free)} 次语音答题`,
      `每天最多 ${String(spokenAllowance.free)} 次語音答題`,
    ),
    // No figure for Pro, as Hugh asked on 2026-10-28 (CB-85).
    pro: words(
      "More spoken answers each day",
      "每天更多语音答题机会",
      "每天更多語音答題機會",
    ),
  },
  {
    id: "lists",
    name: words("Lists 词单 in progress", "进行中的词单", "進行中的詞單"),
    free: words(
      `${String(freeStartedLists)} List in progress at a time`,
      `最多同时有 ${String(freeStartedLists)} 个进行中的词单`,
      `最多同時有 ${String(freeStartedLists)} 個進行中的詞單`,
    ),
    pro: words(
      "Any number of Lists in progress",
      "进行中的词单数量不限",
      "進行中的詞單數量不限",
    ),
  },
  {
    id: "priority",
    name: words(
      "Prioritise words and example sentences",
      "将词语和例句设为优先",
      "將詞語和例句設為優先",
    ),
    free: notIncluded,
    pro: words(
      "Choose which new words and example sentences Review introduces first",
      "选择优先在复习中出现的新词和例句",
      "選擇優先在複習中出現的新詞和例句",
    ),
  },
  {
    id: "paste-import",
    name: words("Import by pasting", "粘贴导入", "貼上匯入"),
    free: words(
      `${grouped(freeImportRows)} rows per import`,
      `每次 ${grouped(freeImportRows)} 行`,
    ),
    pro: words(
      `${grouped(proImportRows)} rows per import`,
      `每次 ${grouped(proImportRows)} 行`,
    ),
  },
  {
    id: "file-import",
    name: words("File imports", "文件导入", "檔案匯入"),
    free: words("No file imports", "不支持文件导入", "不支援檔案匯入"),
    pro: words(
      `Up to ${String(uploadFileBytes / 1_000_000)} MB per file`,
      `每个文件最大 ${String(uploadFileBytes / 1_000_000)} MB`,
      `每個檔案最大 ${String(uploadFileBytes / 1_000_000)} MB`,
    ),
  },
  {
    id: "history",
    name: words("Lookup history", "查询记录", "查詢記錄"),
    free: historyCell(freeHistory),
    pro: historyCell(proHistory),
  },
  {
    id: "huigu",
    name: words("Study summary", "学习总结", "學習總結"),
    free: words("On the site", "在网站上查看", "在網站上查看"),
    pro: words(
      "On the site and by email",
      "在网站上查看，也可通过邮件接收",
      "在網站上查看，也可透過郵件接收",
    ),
  },
  {
    id: "backup",
    name: words(
      "Vocabulary backup by email",
      "通过邮件接收词汇备份",
      "透過郵件接收詞彙備份",
    ),
    free: notIncluded,
    pro: words("Weekly or monthly", "每周或每月", "每週或每月"),
  },
  {
    id: "booklet",
    name: words("Revision booklets", "复习手册", "複習手冊"),
    free: notIncluded,
    pro: words(
      "Print booklets from your saved words, your own Lists or any List",
      "用收藏的词、自己的词单或任何词单打印复习手册",
      "用收藏的詞、自己的詞單或任何詞單列印複習手冊",
    ),
  },
  {
    id: "export",
    name: words("Export to CSV or TSV", "导出 CSV 或 TSV", "匯出 CSV 或 TSV"),
    free: words("Always free", "永久免费", "永久免費"),
    pro: words("Always free", "永久免费", "永久免費"),
  },
];

/**
 * One row of the table.
 * @param id - Which.
 * @returns The row.
 */
export function proFeature(id: ProFeatureId): ProFeature {
  const found = proFeatures.find((feature) => feature.id === id);

  if (found === undefined) {
    throw new Error(`No Pro feature ${id}`);
  }

  return found;
}

/**
 * The rows a free account has no share of, which the menus mark "Pro"
 * (CB-61).
 *
 * Read off the table rather than listed, so a row whose free cell becomes
 * {@link notIncluded} gains the mark and a row that gains a free taster loses
 * it. Pronunciation feedback is the second case: it was meant to be marked
 * once released, and was released with five free checks a day, so it is not.
 * A page says which row it is with `pro_feature` in its front matter, and
 * `partials/pro/tag.html` draws the mark where the row is one of these.
 */
export const proOnlyFeatures: readonly ProFeatureId[] = proFeatures
  .filter((feature) => feature.free === notIncluded)
  .map((feature) => feature.id);

/** What the mark on a Pro-only tool says. */
export const proTagLabel = "Pro";

/**
 * The address an offer links, carrying where it was pressed.
 * @param placement - Where the offer is drawn.
 * @param feature - The row it is about, whose section on `/pro/` it opens at.
 * @returns A path on the site.
 */
export function proOfferHref(
  placement: OfferPlacement,
  feature?: ProFeatureId,
): string {
  return `${proOfferPath}?from=${placement}${feature === undefined ? "" : `#${feature}`}`;
}

/**
 * The address of the practice a panel offers, carrying where it was pressed.
 *
 * The same `from` parameter an offer of Pro carries, on an Exercise, a drill
 * or the worksheet generator instead of on `/pro/`. A fragment stays last,
 * where the generator reads its settings.
 * @param path - The practice's own address, which may end in a fragment.
 * @param placement - Which kind of page the panel is on.
 * @returns The address with the placement on its query.
 */
export function practiceHref(
  path: string,
  placement: PracticePlacement,
): string {
  const at = path.indexOf("#");
  const bare = at < 0 ? path : path.slice(0, at);
  const fragment = at < 0 ? "" : path.slice(at);

  return `${bare}${bare.includes("?") ? "&" : "?"}from=${placement}${fragment}`;
}

/**
 * A table cell as it reads after a colon, "No daily limit" becoming "no daily
 * limit". A cell opening on a figure or a unit is left as it is.
 */
function inLine(cell: string): string {
  return /^[A-Z][a-z]/u.test(cell)
    ? `${cell.charAt(0).toLowerCase()}${cell.slice(1)}`
    : cell;
}

/**
 * `Free: X. Pro: Y.` for one row, which is what every offer on a page says.
 * @param id - The row.
 * @returns The line in English and both writings.
 */
export function proOfferLine(id: ProFeatureId): OfferWords {
  const { free, pro } = proFeature(id);

  return words(
    `Free: ${inLine(free.english)}. Pro: ${inLine(pro.english)}.`,
    `免费：${free.simplified}。Pro：${pro.simplified}。`,
    `免費：${free.traditional}。Pro：${pro.traditional}。`,
  );
}

/** What the link after a line says. */
export const seePro = words("About Pro", "了解 Pro 专业", "了解 Pro 專業");

/** The emails the site sends, each of which ends on a tier line. */
export type OfferEmail =
  | "welcome"
  | "huigu"
  | "backup"
  | "due"
  | "win-back"
  | "start-studying"
  | "keep-studying";

/** A link in an email, by its words and the path it goes to. */
export interface OfferLink {
  readonly label: string;
  readonly path: string;
}

/** The quiet line at the foot of an email, and its link where it has one. */
export interface TierLine {
  readonly said: string;
  readonly link?: OfferLink;
}

/**
 * The line every email ends on, by the reader's tier.
 *
 * Hugh decided on 18 October 2026 that every email the site sends carries one,
 * a line at the foot and never a banner. A free reader is told what a Pro
 * account gets and given the link. A member is thanked and told what their
 * account gives them, with nothing to press.
 *
 * Emails are English, and every Chinese term carries its reading, because the
 * welcome email may reach somebody on their first day of Chinese and the line
 * is the same in all of them.
 * @param properties - Whether the reader holds Pro, and which email it ends.
 * @returns The line.
 */
export function emailTierLine(properties: {
  pro: boolean;
  email: OfferEmail;
}): TierLine {
  if (properties.pro) {
    return {
      said: `Thanks for supporting Chinese Boost. With Pro, you can save words with no limit. You can review in 复习 (fùxí) and start new Exercises with no daily limits. Pronunciation gives you feedback on your sounds and tones, with ${String(dailyPronunciationAttempts)} checks a day. Voice gives you more spoken answers each day to practise recalling words aloud. You can upload word-list files and get vocabulary backups by email.`,
    };
  }

  return {
    said: `With Pro, you can save words with no limit. You can review in 复习 (fùxí) and start new Exercises with no daily limits. Pronunciation gives you feedback on your sounds and tones, with ${String(dailyPronunciationAttempts)} checks a day. Voice gives you more spoken answers each day to practise recalling words aloud. You also get your study summary by email.`,
    link: {
      label: seePro.english,
      path: proOfferHref(`email-${properties.email}`),
    },
  };
}
